import React from "react"

import { Badge, Tooltip } from "@coval/ui"

interface AgentVersionTagProps {
  /** Agent version number the run executed against (`Run.agent_version`). */
  ranVersion?: number | null
  /** The agent's current (live) version number. */
  currentVersion?: number | null
  /**
   * "full" renders a labelled pill ("Ran with v2 — current is v4"); "compact" renders a small
   * version chip ("v2") for dense surfaces. Both share one tooltip.
   */
  variant?: "full" | "compact"
  className?: string
}

/**
 * A run reflects the agent version it executed against, which can fall behind the live agent under
 * the copy-on-save model. The tag flags that gap so an old run is never misread as the current agent.
 *
 * Stale only when both versions are known and differ — runs whose agent predates versioning carry no
 * version number and render nothing.
 */
export function isAgentVersionStale(
  ranVersion?: number | null,
  currentVersion?: number | null,
): boolean {
  return (
    ranVersion != null &&
    currentVersion != null &&
    ranVersion !== currentVersion
  )
}

/**
 * Warning indicator shown when a run executed against a stale agent version. Renders nothing when the
 * run is on the agent's current version (see isAgentVersionStale).
 */
export function AgentVersionTag({
  ranVersion,
  currentVersion,
  variant = "full",
  className,
}: AgentVersionTagProps): React.ReactElement | null {
  if (!isAgentVersionStale(ranVersion, currentVersion)) {
    return null
  }

  const tooltip = `This run used agent v${ranVersion}. The agent is now on v${currentVersion}.`

  if (variant === "compact") {
    return (
      <Tooltip content={tooltip}>
        <Badge variant="warning" className={`shrink-0 ${className ?? ""}`}>
          v{ranVersion}
        </Badge>
      </Tooltip>
    )
  }

  return (
    <Tooltip content={tooltip}>
      <Badge variant="warning" className={className}>
        Ran with v{ranVersion} — current is v{currentVersion}
      </Badge>
    </Tooltip>
  )
}
